import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { CreditCard, CheckCircle2, Calendar, Sparkles, Pause, Play, RotateCcw, X } from 'lucide-react';
import { safeConfirm } from '../../lib/confirm';

/**
 * Patient view of their active visit packs + memberships.
 * Each subscription renders as a credit-card-style tile.
 * Self-service hold (1w–3mo) + self-renewal.
 */
export default function PatientMembership() {
  const [subs, setSubs] = useState([]);
  const [me, setMe] = useState(null);
  const [holdOptions, setHoldOptions] = useState([]);
  const [holdSub, setHoldSub] = useState(null);

  const load = () => api.get('/memberships/my').then(r => setSubs(r.data));
  useEffect(() => {
    api.get('/auth/me').then(r => setMe(r.data));
    load();
    api.get('/memberships/hold-options').then(r => setHoldOptions(r.data)).catch(() => {});
  }, []);

  const active = subs.filter(s => s.status === 'active' || s.status === 'held');
  const past = subs.filter(s => s.status !== 'active' && s.status !== 'held');

  const resume = async (s) => {
    await api.post(`/memberships/${s.membership_id}/resume`);
    load();
  };
  const renew = async (s) => {
    if (!await safeConfirm(`Renew ${s.plan_name} for another ${s.duration_months} months?`)) return;
    await api.post(`/memberships/${s.membership_id}/renew`);
    load();
  };

  return (
    <div className="p-6 md:p-8 max-w-5xl">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">MY CLINIC CARD</div>
        <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight">Memberships</h1>
        <p className="text-fg-muted mt-2">Your visit packs and monthly memberships at Physiogenic.</p>
      </div>

      {!active.length && !past.length && (
        <div className="surface border border-soft rounded-lg p-8 text-center">
          <CreditCard className="w-10 h-10 text-fg-muted mx-auto"/>
          <h3 className="font-heading font-semibold mt-3">No memberships yet</h3>
          <p className="text-sm text-fg-muted mt-1">Ask reception about visit packs and monthly memberships for discounted sessions.</p>
        </div>
      )}

      {active.length > 0 && (
        <>
          <h2 className="text-sm font-heading font-semibold mb-3 uppercase tracking-wider text-fg-muted">Active</h2>
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            {active.map(s => (
              <Card key={s.membership_id} sub={s} patientName={me?.name}
                    onHold={() => setHoldSub(s)} onResume={() => resume(s)} onRenew={() => renew(s)}/>
            ))}
          </div>
        </>
      )}

      {past.length > 0 && (
        <>
          <h2 className="text-sm font-heading font-semibold mb-3 uppercase tracking-wider text-fg-muted">History</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {past.map(s => (
              <Card key={s.membership_id} sub={s} patientName={me?.name} faded onRenew={() => renew(s)}/>
            ))}
          </div>
        </>
      )}

      {holdSub && (
        <HoldModal sub={holdSub} options={holdOptions}
                   onClose={() => setHoldSub(null)} onSaved={() => { load(); setHoldSub(null); }}/>
      )}
    </div>
  );
}

function Card({ sub, patientName, faded, onHold, onResume, onRenew }) {
  const isPack = sub.kind === 'visit_pack';
  const isHeld = sub.status === 'held';
  const isExpired = sub.status === 'expired';
  const total = sub.visits_total || 0;
  const used = sub.visits_used || 0;
  const remaining = total - used;
  const pct = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;
  const bg = sub.card_color || '#003D80';
  return (
    <div className={`rounded-2xl p-6 text-white shadow-lg relative overflow-hidden ${faded ? 'opacity-60' : ''}`}
         style={{ background: `linear-gradient(135deg, ${bg} 0%, ${bg}cc 100%)` }}
         data-testid={`card-${sub.membership_id}`}>
      <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-white/10"></div>
      <div className="absolute top-3 right-4">
        {isPack ? <CheckCircle2 className="w-5 h-5 text-white/70"/> : <Sparkles className="w-5 h-5 text-white/70"/>}
      </div>
      <div className="relative z-10">
        <div className="text-[10px] uppercase tracking-[0.2em] text-white/70 font-medium">{isPack ? 'VISIT PACK' : 'MEMBERSHIP'}</div>
        <h3 className="font-heading font-bold text-xl mt-1">{sub.plan_name}</h3>
        <div className="text-xs text-white/70 mt-1">{patientName}</div>

        <div className="mt-6">
          {isPack ? (
            <>
              <div className="flex items-end justify-between">
                <div>
                  <div className="text-3xl font-heading font-bold">{remaining}</div>
                  <div className="text-[11px] text-white/70 uppercase tracking-wider">visits left</div>
                </div>
                <div className="text-right">
                  <div className="text-sm">{used} / {total}</div>
                  <div className="text-[10px] text-white/70">used / total</div>
                </div>
              </div>
              <div className="mt-3 h-2 bg-white/20 rounded-full overflow-hidden">
                <div className="h-2 bg-white" style={{ width: `${pct}%` }} data-testid={`progress-${sub.membership_id}`}></div>
              </div>
            </>
          ) : (
            <div className="flex items-end justify-between">
              <div>
                <div className="text-3xl font-heading font-bold">{sub.days_left ?? '—'}</div>
                <div className="text-[11px] text-white/70 uppercase tracking-wider">{isHeld ? 'days until resume' : 'days left'}</div>
              </div>
              <div className="text-right text-[11px] text-white/80">
                <Calendar className="w-3 h-3 inline mr-1"/>
                {sub.start_date} → {sub.expiry_date}
              </div>
            </div>
          )}
        </div>

        {isHeld && sub.hold_until && (
          <div className="mt-4 px-3 py-2 rounded-md bg-white/15 border border-white/20 text-[11px]">
            <Pause className="w-3 h-3 inline mr-1"/> On hold until <strong>{sub.hold_until.slice(0, 10)}</strong> — auto-resumes that day.
          </div>
        )}

        <div className="mt-5 flex items-center justify-between text-[11px] text-white/70 flex-wrap gap-2">
          <span className="font-mono">ID · {sub.membership_id?.slice(4, 14).toUpperCase()}</span>
          <div className="flex items-center gap-2">
            <span className="capitalize">{sub.status}</span>
            {/* Action buttons */}
            {!isPack && !isHeld && !isExpired && !faded && (
              <button onClick={onHold} className="ml-2 px-2 py-1 rounded-md bg-white/20 hover:bg-white/30 text-[11px] inline-flex items-center gap-1" data-testid={`hold-${sub.membership_id}`}>
                <Pause className="w-3 h-3"/> Hold
              </button>
            )}
            {isHeld && (
              <button onClick={onResume} className="ml-2 px-2 py-1 rounded-md bg-white/30 hover:bg-white/40 text-[11px] inline-flex items-center gap-1" data-testid={`resume-${sub.membership_id}`}>
                <Play className="w-3 h-3"/> Resume now
              </button>
            )}
            {!isPack && (isExpired || sub.days_left <= 14) && onRenew && (
              <button onClick={onRenew} className="ml-2 px-2 py-1 rounded-md bg-[#FFB000] text-[#003D80] hover:bg-[#FFC833] text-[11px] inline-flex items-center gap-1 font-semibold" data-testid={`renew-${sub.membership_id}`}>
                <RotateCcw className="w-3 h-3"/> Renew
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function HoldModal({ sub, options, onClose, onSaved }) {
  const [duration, setDuration] = useState('1w');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/memberships/${sub.membership_id}/hold`, { duration_id: duration, reason });
      onSaved();
    } catch (e) {
      alert('Could not hold: ' + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose} data-testid="hold-modal">
      <div className="surface border border-soft rounded-xl shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-soft flex items-center justify-between">
          <h2 className="font-heading font-semibold inline-flex items-center gap-2"><Pause className="w-4 h-4"/> Hold membership</h2>
          <button onClick={onClose} className="text-fg-muted hover:text-fg"><X className="w-5 h-5"/></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-sm text-fg-muted">Pause <strong>{sub.plan_name}</strong> for a fixed period. Your paid days are extended automatically — you won't lose time.</p>
          <label className="block">
            <div className="text-xs text-fg-muted mb-1">Hold duration</div>
            <select className="input" value={duration} onChange={e => setDuration(e.target.value)} data-testid="hold-duration">
              {(options || []).map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </label>
          <label className="block">
            <div className="text-xs text-fg-muted mb-1">Reason (optional)</div>
            <textarea className="input min-h-[60px]" value={reason} onChange={e => setReason(e.target.value)} placeholder="Vacation, injury recovery, work travel…" data-testid="hold-reason"/>
          </label>
          <div className="text-[11px] text-fg-muted bg-[#FFF4D6] border border-[#FFB000]/30 rounded p-2">
            <strong>Hold policy:</strong> max 3 months at a time; auto-resumes on the end date. You can resume early at any time.
          </div>
        </div>
        <div className="px-5 pb-5 flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
          <button onClick={submit} disabled={busy} className="btn-primary text-sm" data-testid="hold-confirm">
            {busy ? 'Saving…' : 'Place on hold'}
          </button>
        </div>
      </div>
    </div>
  );
}
